import React, { useState } from "react";
import { SearchResult } from "../../services/searchService";
import { stripMarkdown } from "@/utils/stripMarkdown";
import {
  CalendarIcon,
  DocumentTextIcon,
  NewspaperIcon,
  VideoCameraIcon,
  MicrophoneIcon,
  DocumentIcon,
  BookOpenIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";

interface SearchResultCardProps {
  result: SearchResult;
  type: "event" | "resource";
}

const SearchResultCard: React.FC<SearchResultCardProps> = ({
  result,
  type,
}) => {
  const [imageError, setImageError] = useState(false);

  const handleClick = () => {
    // Every result opens in a new tab; the modal stays open with its results
    // so the user can keep evaluating other matches without re-searching.
    window.open(result.url, "_blank", "noopener,noreferrer");
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const getResourceIcon = (resourceType?: string) => {
    switch (resourceType) {
      case "News":
        return <NewspaperIcon className="h-5 w-5" />;
      case "Video":
        return <VideoCameraIcon className="h-5 w-5" />;
      case "Podcast":
        return <MicrophoneIcon className="h-5 w-5" />;
      case "Article":
        return <BookOpenIcon className="h-5 w-5" />;
      case "Partner Page":
        return <UserGroupIcon className="h-5 w-5" />;
      case "File":
        return <DocumentIcon className="h-5 w-5" />;
      default:
        return <DocumentTextIcon className="h-5 w-5" />;
    }
  };

  const getHighlightedText = (
    text?: string,
    highlightResult?: { value?: string },
  ) => {
    if (!text) return "";

    // If we have highlight results from Algolia, use them. The value can carry
    // raw Markdown emphasis markers (e.g. **bold**) which would otherwise show
    // as literal asterisks — strip them while preserving the <mark> tags.
    if (highlightResult?.value) {
      return (
        <span
          className="[&>mark]:bg-yellow-200 [&>mark]:text-gray-900"
          dangerouslySetInnerHTML={{
            __html: stripMarkdown(highlightResult.value),
          }}
        />
      );
    }

    return stripMarkdown(text);
  };

  const renderThumbnail = () => {
    const showImage = result.thumbnail && !imageError;

    if (showImage) {
      return (
        <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden bg-gray-100">
          <img
            alt=""
            className="w-full h-full object-cover"
            onError={handleImageError}
            src={result.thumbnail}
          />
        </div>
      );
    }

    // Fallback to icon with ResourceType colors
    const iconElement =
      type === "event" ? (
        <CalendarIcon className="h-6 w-6 sm:h-8 sm:w-8" />
      ) : result.primaryIcon ? (
        <div
          className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center [&>svg]:w-full [&>svg]:h-full"
          dangerouslySetInnerHTML={{ __html: result.primaryIcon }}
        />
      ) : (
        <div className="h-6 w-6 sm:h-8 sm:w-8">
          {getResourceIcon(result.resourceType)}
        </div>
      );

    return (
      <div
        className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg flex items-center justify-center"
        style={{
          backgroundColor: result.backgroundColor || "#f3f4f6",
          color: result.textColor || "#6b7280",
          border: result.borderColor
            ? `2px solid ${result.borderColor}`
            : undefined,
        }}
      >
        {iconElement}
      </div>
    );
  };

  return (
    <div
      className="group relative rounded-lg border border-gray-200 bg-white hover:border-gray-300 hover:shadow-lg transition-all cursor-pointer"
      onClick={handleClick}
    >
      <div className="flex items-start space-x-3 sm:space-x-4 p-3 sm:p-4">
        {/* Thumbnail or Icon */}
        {renderThumbnail()}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Type Badge and Title */}
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h4 className="text-sm sm:text-base font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">
                {type === "event" ? (
                  <>
                    {getHighlightedText(
                      result.eventName,
                      result._highlightResult?.eventName,
                    )}
                  </>
                ) : (
                  <>
                    {getHighlightedText(
                      result.title,
                      result._highlightResult?.title,
                    )}
                  </>
                )}
              </h4>
            </div>
          </div>

          {/* Description */}
          <p className="mt-1 text-sm text-gray-600 line-clamp-2">
            {type === "event" ? (
              <>
                {getHighlightedText(
                  result.eventDescription,
                  result._highlightResult?.eventDescription,
                )}
              </>
            ) : (
              <>
                {getHighlightedText(
                  result.searchDescription,
                  result._highlightResult?.searchDescription,
                )}
              </>
            )}
          </p>

          {/* Metadata */}
          <div className="mt-3 flex items-center flex-wrap gap-2 text-xs">
            {type === "event" ? (
              <>
                {result.eventType && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {result.eventType}
                  </span>
                )}
                {result.eventCategory && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {result.eventCategory}
                  </span>
                )}
                {result.eventPrice !== undefined && result.eventPrice > 0 && (
                  <span className="text-gray-600 font-medium">
                    ${result.eventPrice}
                  </span>
                )}
                {result.sessions && result.sessions.length > 0 && (
                  <span className="text-gray-500">
                    {result.sessions.length} sessions
                  </span>
                )}
              </>
            ) : (
              <>
                {result.resourceType && (
                  <span
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: result.backgroundColor || "#dcfce7",
                      color: result.textColor || "#166534",
                      border: result.borderColor
                        ? `1px solid ${result.borderColor}`
                        : undefined,
                    }}
                  >
                    {result.resourceType}
                  </span>
                )}
                {result.categories && result.categories.length > 0 && (
                  <span className="text-gray-500">{result.categories[0]}</span>
                )}
              </>
            )}

            {/* View Count */}
            {result.viewCount !== undefined && result.viewCount > 0 && (
              <span className="text-gray-500">
                {result.viewCount.toLocaleString()} views
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResultCard;
